import axios from "axios";
import { PDA_PREFIX } from "../../global/constants";
import { toast } from "react-toastify";
import { getLibraryStructures } from "../structure-services/StructureActions";

// A top-level row's parentId comes from Bryntum as the store's root node id
// (e.g. "store-525-rootNode"), which the backend's Long parentId can't parse
// (400). Coerce anything non-numeric to null so a root structure is sent with
// no parent instead of a phantom node id.
export const toNumericIdOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) ? n : null;
};

export const GET_ALL_SCOPE_GROUPS_REQUEST = "GET_ALL_SCOPE_GROUPS_REQUEST";
export const GET_ALL_SCOPE_GROUPS_SUCCESS = "GET_ALL_SCOPE_GROUPS_SUCCESS";
export const GET_ALL_SCOPE_GROUPS_ERROR = "GET_ALL_SCOPE_GROUPS_ERROR";
export const ADD_SCOPE_GROUP_REQUEST = "ADD_SCOPE_GROUP_REQUEST";
export const ADD_SCOPE_GROUP_SUCCESS = "ADD_SCOPE_GROUP_SUCCESS";
export const ADD_SCOPE_GROUP_ERROR = "ADD_SCOPE_GROUP_ERROR";
export const UPDATE_SCOPE_GROUP_REQUEST = "UPDATE_SCOPE_GROUP_REQUEST";
export const UPDATE_SCOPE_GROUP_SUCCESS = "UPDATE_SCOPE_GROUP_SUCCESS";
export const UPDATE_SCOPE_GROUP_ERROR = "UPDATE_SCOPE_GROUP_ERROR";
export const DELETE_SCOPE_GROUP_REQUEST = "DELETE_SCOPE_GROUP_REQUEST";
export const DELETE_SCOPE_GROUP_SUCCESS = "DELETE_SCOPE_GROUP_SUCCESS";
export const DELETE_SCOPE_GROUP_ERROR = "DELETE_SCOPE_GROUP_ERROR";

const getAllScopeGroupsRequest = () => ({
  type: GET_ALL_SCOPE_GROUPS_REQUEST,
});

const getAllScopeGroupsSuccess = (data) => ({
  type: GET_ALL_SCOPE_GROUPS_SUCCESS,
  payload: data,
});

const getAllScopeGroupsError = (err) => ({
  type: GET_ALL_SCOPE_GROUPS_ERROR,
  payload: err,
});

// `nonEmptyOnly` is used by the Generative Workspace Scope tab: the backend
// prunes breakdown structures whose whole subtree has no library structure
// linked, so empty branches never reach the tree.
export const getAllScopeGroups = ({ nonEmptyOnly = false } = {}) => async (
  dispatch
) => {
  dispatch(getAllScopeGroupsRequest());
  try {
    const res = await axios.get(`${PDA_PREFIX}/breakdown-structure`, {
      params: nonEmptyOnly ? { nonEmptyOnly: true } : undefined,
    });
    dispatch(getAllScopeGroupsSuccess(res.data));
  } catch (err) {
    dispatch(getAllScopeGroupsError(err));
    toast.error("Something went wrong. Please contact support");
  }
};

const updateScopeGroupRequest = () => ({
  type: UPDATE_SCOPE_GROUP_REQUEST,
});

const updateScopeGroupSuccess = (data) => ({
  type: UPDATE_SCOPE_GROUP_SUCCESS,
  payload: data,
});

const updateScopeGroupError = (err) => ({
  type: UPDATE_SCOPE_GROUP_ERROR,
  payload: err,
});

export const updateScopeGroup = (data) => async (dispatch) => {
  dispatch(updateScopeGroupRequest());
  try {
    const payload = { ...data, parentId: toNumericIdOrNull(data.parentId) };
    const res = await axios.put(`${PDA_PREFIX}/breakdown-structure`, payload);
    dispatch(updateScopeGroupSuccess(res.data));
  } catch (err) {
    dispatch(updateScopeGroupError(err));
    if (err.response.status === 400) toast.error(err.response.data);
    else
      toast.error(
        "Something went wrong during creation. Please contact support"
      );
  }
};

const deleteScopeGroupRequest = () => ({
  type: DELETE_SCOPE_GROUP_REQUEST,
});

const deleteScopeGroupSuccess = (data) => ({
  type: DELETE_SCOPE_GROUP_SUCCESS,
  payload: data,
});

const deleteScopeGroupError = (err) => ({
  type: DELETE_SCOPE_GROUP_ERROR,
  payload: err,
});

export const deleteScopeGroup = (id) => async (dispatch) => {
  dispatch(deleteScopeGroupRequest());
  try {
    await axios.delete(`${PDA_PREFIX}/breakdown-structure/${id}`);
    dispatch(deleteScopeGroupSuccess(id));
    toast.success("Deletion done !");
  } catch (err) {
    dispatch(deleteScopeGroupError(err));
    toast.error("Something went wrong. Please contact support");
  }
};

// Sequentially delete N breakdown structures via the existing
// single-delete endpoint (each call unlinks the BS from every attached
// library structure via `deleteAllStructureAssignmentsById` — so the
// library scopes stay intact, only the BS row goes away). One refresh
// + one summary toast at the end so the user isn't spammed with N
// "Deletion done !" toasts.
export const bulkDeleteScopeGroups = (ids) => async (dispatch) => {
  if (!Array.isArray(ids) || ids.length === 0) return;
  dispatch(deleteScopeGroupRequest());
  const uniqueIds = Array.from(new Set(ids.filter((id) => id != null)));
  let deleted = 0;
  const failed = [];
  for (const id of uniqueIds) {
    try {
      await axios.delete(`${PDA_PREFIX}/breakdown-structure/${id}`);
      dispatch(deleteScopeGroupSuccess(id));
      deleted += 1;
    } catch (err) {
      failed.push(id);
    }
  }
  await dispatch(getAllScopeGroups());
  if (failed.length === 0) {
    toast.success(
      `${deleted} structure${deleted > 1 ? "s" : ""} deleted.`
    );
  } else if (deleted === 0) {
    toast.error("None of the selected structures could be deleted.");
  } else {
    toast.warn(
      `${deleted} structure${deleted > 1 ? "s" : ""} deleted, ${failed.length} failed.`
    );
  }
};

const addScopeGroupRequest = () => ({
  type: ADD_SCOPE_GROUP_REQUEST,
});

const addScopeGroupSuccess = (data) => ({
  type: ADD_SCOPE_GROUP_SUCCESS,
  payload: data,
});

const addScopeGroupError = (err) => ({
  type: ADD_SCOPE_GROUP_ERROR,
  payload: err,
});

export const addScopeGroup = (data) => async (dispatch) => {
  dispatch(addScopeGroupRequest());
  try {
    const payload = { ...data, parentId: toNumericIdOrNull(data.parentId) };
    const res = await axios.post(`${PDA_PREFIX}/breakdown-structure`, payload);
    dispatch(addScopeGroupSuccess(res.data));
    toast.success("Group created !");
  } catch (err) {
    dispatch(addScopeGroupError(err));
    if (err.response.status === 400) toast.error(err.response.data);
    else
      toast.error(
        "Something went wrong during creation. Please contact support"
      );
  }
};

// Deep-duplicate a breakdown structure: BE clones the row plus every
// descendant + every library-structure attachment on the subtree.
// After success we refetch BOTH the BS list AND the library structures
// — the scope-child column in the tree is driven by
// `structure.breakdownStructures[]` (see buildBreakdownStructureTree),
// so refreshing only the BS list would leave the freshly-cloned BS
// looking like an empty leaf until the next page load.
export const duplicateScopeGroup = (id) => async (dispatch) => {
  try {
    await axios.post(`${PDA_PREFIX}/breakdown-structure/${id}/duplicate`);
    await Promise.all([
      dispatch(getAllScopeGroups()),
      dispatch(getLibraryStructures()),
    ]);
    toast.success("Structure duplicated.");
  } catch (err) {
    toast.error(
      err?.response?.data ||
        "Something went wrong while duplicating the structure. Please contact support."
    );
  }
};

// Reparent + reorder a breakdown structure. Bryntum already applied the
// move locally when this fires; on BE failure (cycle, depth guard, etc.)
// we refetch the whole list so the FE tree snaps back to persisted state.
export const moveScopeGroup = (id, newParentId, newIndex) => async (dispatch) => {
  try {
    await axios.put(`${PDA_PREFIX}/breakdown-structure/${id}/move`, {
      newParentId: newParentId ?? null,
      newIndex,
    });
  } catch (err) {
    toast.error(
      err?.response?.data ||
        "Move rejected. Reloading structures."
    );
    dispatch(getAllScopeGroups());
  }
};

// Reparent a scope (library-structure attachment) from one BS to
// another. The `getLibraryStructures` refetch is what surfaces the new
// attachment in the tree — `getAllScopeGroups` only refreshes BS rows.
export const moveScopeAttachment = (
  structureId, sourceBsId, targetBsId
) => async (dispatch) => {
  if (sourceBsId === targetBsId) return;
  try {
    await axios.put(`${PDA_PREFIX}/breakdown-structure/move-scope`, {
      structureId,
      sourceBsId,
      targetBsId,
    });
    await dispatch(getLibraryStructures());
    toast.success("Scope moved.");
  } catch (err) {
    toast.error(err?.response?.data || "Move rejected. Reloading scopes.");
    await dispatch(getLibraryStructures());
  }
};

// Duplicate a scope inside its BS: BE clones the library Structure +
// every Block, then attaches the clone to the same BS with the source's
// start/finish/quantity.
export const duplicateScopeAttachment = (
  bsId, structureId
) => async (dispatch) => {
  try {
    await axios.post(
      `${PDA_PREFIX}/breakdown-structure/${bsId}/scope/${structureId}/duplicate`
    );
    await dispatch(getLibraryStructures());
    toast.success("Scope duplicated.");
  } catch (err) {
    toast.error(
      err?.response?.data ||
        "Something went wrong while duplicating the scope. Please contact support."
    );
  }
};

// Drag-and-drop "duplicate": attaches the same scope (same code) to an
// additional parent BS as a brand new join row — the original attachment
// under its source BS is never touched. Idempotent on the BE, so dropping
// the same scope onto the same parent twice is a no-op, not an error.
// Refetches both slices: getAllScopeGroups carries the new attachment's
// position, getLibraryStructures carries the link itself.
export const attachScopeToBreakdownStructure = (
  structureId, targetBsId
) => async (dispatch) => {
  try {
    await axios.put(
      `${PDA_PREFIX}/breakdown-structure/${targetBsId}/scope/${structureId}/attach`
    );
    await Promise.all([
      dispatch(getAllScopeGroups()),
      dispatch(getLibraryStructures()),
    ]);
  } catch (err) {
    toast.error(err?.response?.data || "Could not attach the scope. Reloading structures.");
    await Promise.all([
      dispatch(getAllScopeGroups()),
      dispatch(getLibraryStructures()),
    ]);
  }
};

// Removes exactly one (bsId, structureId) attachment — the scope-row
// delete button in the Structures grid. Never deletes the scope itself,
// and never touches any OTHER BS the same scope is still attached to.
// Refetches both slices for the same reason attachScopeToBreakdownStructure
// does: getAllScopeGroups carries the BS's own child count, getLibraryStructures
// carries the scope's breakdownStructures[] membership the tree renders from.
export const removeScopeFromBreakdownStructure = (
  bsId, structureId
) => async (dispatch) => {
  try {
    await axios.delete(
      `${PDA_PREFIX}/breakdown-structure/${bsId}/scope/${structureId}`
    );
    await Promise.all([
      dispatch(getAllScopeGroups()),
      dispatch(getLibraryStructures()),
    ]);
    toast.success("Scope removed from structure.");
  } catch (err) {
    toast.error(err?.response?.data || "Could not remove the scope. Reloading structures.");
    await Promise.all([
      dispatch(getAllScopeGroups()),
      dispatch(getLibraryStructures()),
    ]);
  }
};

// Persists the drag-reorder of a BS's scope children in one batch call.
export const reorderScopesInBreakdownStructure = (
  bsId, orderedStructureIds
) => async (dispatch) => {
  try {
    await axios.put(
      `${PDA_PREFIX}/breakdown-structure/${bsId}/scopes/order`,
      orderedStructureIds
    );
    await dispatch(getAllScopeGroups());
  } catch (err) {
    toast.error(err?.response?.data || "Reorder rejected. Reloading structures.");
    await dispatch(getAllScopeGroups());
  }
};

// Renames a scope in the context of one parent BS. The BE decides whether
// this updates the canonical Structure.name (single-parent scope) or just
// this parent's display_name override (multi-parent scope) — either way
// we refetch both slices rather than trying to patch the right one locally.
export const renameScopeInBreakdownStructure = (
  bsId, structureId, newName
) => async (dispatch) => {
  try {
    await axios.put(
      `${PDA_PREFIX}/breakdown-structure/${bsId}/scope/${structureId}/name`,
      { displayName: newName }
    );
    await Promise.all([
      dispatch(getAllScopeGroups()),
      dispatch(getLibraryStructures()),
    ]);
  } catch (err) {
    toast.error(err?.response?.data || "Rename rejected. Reloading structures.");
    await Promise.all([
      dispatch(getAllScopeGroups()),
      dispatch(getLibraryStructures()),
    ]);
  }
};
